/**
 * Account → Payments (2026-06-27) — per-tenant Stripe Connect + pay-by-invoice config.
 *
 * Owner-gated self-service:
 *  - Connect / disconnect the org's OWN Stripe account (Standard Connect, OAuth). Customers then
 *    pay invoices directly into the org's Stripe (no platform fee). Money never routes through BEAM.
 *  - Configure the public pay-by-invoice page: a short link (slug) on getabeam.com/pay/<slug>, an
 *    optional custom domain (CNAME), and an embeddable snippet for the org's own website.
 *
 * Every control performs a real backend round-trip via @beam/api-types (TanStack Query). No card
 * data or keys cross the app (mobile invariant 2). NEVER imports @smartbox/* (invariant 1).
 */
import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPaymentsStatus,
  beginConnect,
  disconnectStripe,
  setPayPage,
  verifyPayDomain,
} from '../../../../src/api/payments';
import { openExternalUrl } from '../../../../src/platform/linking';
import { API_BASE_URL } from '../../../../src/config';
import { Screen, Text, Card, Button, Field, Badge } from '../../../../src/components/ui';
import { theme } from '../../../../src/theme';
import { extractErrorMessage } from '../../../../src/lib/format';

export default function PaymentsScreen() {
  const queryClient = useQueryClient();
  const params = useLocalSearchParams<{ connected?: string; connect_error?: string }>();

  const status = useQuery({ queryKey: ['payments-status'], queryFn: getPaymentsStatus });

  // After the OAuth callback redirects back here (web), refetch so the UI reflects the new state.
  useEffect(() => {
    if (params.connected || params.connect_error) {
      void queryClient.invalidateQueries({ queryKey: ['payments-status'] });
    }
  }, [params.connected, params.connect_error, queryClient]);

  // --- local form state for the pay-page config ---
  const [slug, setSlug] = useState('');
  const [domain, setDomain] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  // Seed the form from server state once loaded.
  useEffect(() => {
    if (status.data) {
      setSlug(status.data.payPage.slug ?? '');
      setDomain(status.data.payPage.customDomain ?? '');
    }
  }, [status.data]);

  const connectMutation = useMutation({
    mutationFn: beginConnect,
    onSuccess: async (r) => {
      if (r.url) await openExternalUrl(r.url);
    },
    onError: (e) => setFormError(extractErrorMessage(e, 'Could not start Stripe connection.')),
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectStripe,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payments-status'] }),
    onError: (e) => setFormError(extractErrorMessage(e, 'Could not disconnect Stripe.')),
  });

  const savePageMutation = useMutation({
    mutationFn: () =>
      setPayPage({ slug: slug.trim() || null, customDomain: domain.trim() || null }),
    onSuccess: () => {
      setSavedNote('Saved');
      void queryClient.invalidateQueries({ queryKey: ['payments-status'] });
    },
    onError: (e) => setFormError(extractErrorMessage(e, 'Could not save the pay-page settings.')),
  });

  const verifyMutation = useMutation({
    mutationFn: verifyPayDomain,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payments-status'] }),
    onError: (e) => setFormError(extractErrorMessage(e, 'Could not verify the domain.')),
  });

  const data = status.data;
  const embedSnippet = useMemo(() => {
    const s = data?.payPage.slug;
    if (!s) return null;
    return `<div id="beam-pay"></div>\n<script src="${API_BASE_URL}/api/pay/${s}/embed.js"></script>`;
  }, [data?.payPage.slug]);

  if (status.isLoading) {
    return (
      <Screen>
        <View style={styles.center}>
          <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading payments" />
        </View>
      </Screen>
    );
  }

  if (status.isError || !data) {
    return (
      <Screen>
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(status.error, 'Failed to load payment settings.')}
          </Text>
          <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => status.refetch()} />
        </Card>
      </Screen>
    );
  }

  return (
    <Screen scroll>
      <Text variant="title">Payments</Text>
      <Text variant="body" color="secondary">
        Connect your own Stripe account so customers can pay your invoices online. The money goes
        straight to your Stripe — BEAM never takes a cut of your invoice payments.
      </Text>

      {params.connect_error ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            Stripe connection didn't complete ({String(params.connect_error)}). Please try again.
          </Text>
        </Card>
      ) : null}

      {/* --- Connection status --- */}
      <Card>
        <Text variant="label" color="secondary">Stripe account</Text>
        {!data.configured ? (
          <Text variant="body" color="secondary">
            Online payments aren't available on this deployment yet. Check back soon.
          </Text>
        ) : !data.connected ? (
          <>
            <Text variant="body" color="secondary">
              Not connected. Connect Stripe to start accepting card payments on your invoices.
            </Text>
            <Button
              title="Connect Stripe"
              loading={connectMutation.isPending}
              disabled={connectMutation.isPending}
              onPress={() => {
                setFormError(null);
                connectMutation.mutate();
              }}
              accessibilityLabel="Connect Stripe"
            />
          </>
        ) : (
          <>
            <View style={styles.row}>
              <Text variant="bodyStrong">Connected</Text>
              {data.status === 'restricted' || !data.chargesEnabled ? (
                <Badge status="inProgress" label="Action needed" />
              ) : (
                <Badge status="paid" label="Active" />
              )}
            </View>
            {data.status === 'restricted' || !data.chargesEnabled ? (
              <Text variant="small" color="secondary">
                Stripe needs more information before you can accept payments. Open Stripe to finish,
                then reconnect.
              </Text>
            ) : (
              <Text variant="small" color="secondary">
                Card payments are enabled. Payouts {data.payoutsEnabled ? 'are' : 'are not yet'} active.
              </Text>
            )}
            <Button
              title="Disconnect"
              variant="secondary"
              size="sm"
              fullWidth={false}
              loading={disconnectMutation.isPending}
              disabled={disconnectMutation.isPending}
              onPress={() => {
                setFormError(null);
                disconnectMutation.mutate();
              }}
              accessibilityLabel="Disconnect Stripe"
            />
          </>
        )}
      </Card>

      {/* --- Pay-by-invoice page --- */}
      <Card>
        <Text variant="label" color="secondary">Pay-by-invoice page</Text>
        <Text variant="small" color="secondary">
          Customers enter an invoice number and pay. Use the BEAM-hosted page, point your own domain
          at it, or embed it on your website.
        </Text>

        <Field
          label="Short link"
          placeholder="e.g. lightwave"
          value={slug}
          onChangeText={(t) => {
            setSlug(t);
            setSavedNote(null);
          }}
          autoCapitalize="none"
          autoCorrect={false}
          accessibilityLabel="Pay page short link"
        />
        {data.payPage.hostedUrl ? (
          <Field
            label="Your hosted pay page"
            value={data.payPage.hostedUrl}
            editable={false}
            selectTextOnFocus
            accessibilityLabel="Hosted pay page URL"
          />
        ) : null}

        <Field
          label="Custom domain (optional)"
          placeholder="pay.yourcompany.com"
          value={domain}
          onChangeText={(t) => {
            setDomain(t);
            setSavedNote(null);
          }}
          autoCapitalize="none"
          autoCorrect={false}
          accessibilityLabel="Custom pay domain"
        />

        <Button
          title="Save"
          loading={savePageMutation.isPending}
          disabled={savePageMutation.isPending}
          onPress={() => {
            setFormError(null);
            savePageMutation.mutate();
          }}
          accessibilityLabel="Save pay page settings"
        />
        {savedNote ? (
          <Text variant="small" color="secondary">{savedNote}</Text>
        ) : null}

        {data.payPage.customDomain ? (
          <View style={styles.subBlock}>
            <View style={styles.row}>
              <Text variant="small" color="secondary">Domain status</Text>
              <Badge
                status={data.payPage.domainVerified ? 'paid' : 'inProgress'}
                label={data.payPage.domainVerified ? 'Verified' : 'Not verified'}
              />
            </View>
            <Text variant="small" color="secondary">
              Add a CNAME record for {data.payPage.customDomain} pointing at your BEAM pay host, then
              verify.
            </Text>
            <Button
              title="Verify domain"
              variant="secondary"
              size="sm"
              fullWidth={false}
              loading={verifyMutation.isPending}
              disabled={verifyMutation.isPending}
              onPress={() => {
                setFormError(null);
                verifyMutation.mutate();
              }}
              accessibilityLabel="Verify custom domain"
            />
            {verifyMutation.data ? (
              <Text variant="small" color="secondary">
                CNAME target: {verifyMutation.data.expected}
                {verifyMutation.data.verified ? ' — verified' : ' — not detected yet'}
              </Text>
            ) : null}
          </View>
        ) : null}
      </Card>

      {/* --- Embed snippet --- */}
      {embedSnippet ? (
        <Card>
          <Text variant="label" color="secondary">Embed on your website</Text>
          <Text variant="small" color="secondary">
            Paste this where you want the "pay an invoice" box to appear.
          </Text>
          <Field
            value={embedSnippet}
            editable={false}
            multiline
            selectTextOnFocus
            accessibilityLabel="Embed snippet"
          />
        </Card>
      ) : null}

      {formError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">{formError}</Text>
        </Card>
      ) : null}
    </Screen>
  );
}

const styles = StyleSheet.create({
  center: { paddingVertical: theme.spacing.lg, alignItems: 'center' },
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing.sm },
  subBlock: { gap: theme.spacing.sm, marginTop: theme.spacing.sm },
});
